import type { User } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icons.js";
import { currentTheme, onTheme } from "../lib/theme.js";
import { ACCOUNT_NAME, openAccountName } from "./accountName.js";
import { openActivity } from "./activity.js";
import { connectionsSection } from "./connections.js";
import { openDeleteAccount } from "./deleteAccount.js";
import { openEmailChange } from "./emailChange.js";
import { policyLinks } from "./policyLinks.js";
import { signOutControl } from "./signOut.js";
import { syncLine, themeToggle, type Screen, type Shell } from "./ui.js";

/**
 * What the Settings page needs from the frame beyond a screen's ordinary
 * `Shell`: the account's own actions, and the two questions the frame alone
 * can answer about whether a form is open. They lived in the frame when these
 * controls were its rail foot and More sheet, and the guards they carry move
 * with them unchanged.
 */
export interface AccountFacet {
  updateUser(user: User): void;
  /** Called after `updateUser` changes the address this device holds. */
  onUser(fn: (user: User) => void): () => void;
  deleteAccount(): Promise<void>;
  /** A popup or a Details question is up. */
  formOpen(): boolean;
  /** As `formOpen`, or the Details panel holds an unsaved draft. */
  draftOpen(): boolean;
}

/**
 * Settings (g48): one page, reached from the gear at the foot of the rail and
 * the Settings tab on a phone. It holds what the rail foot and the More sheet
 * held -- the account, the theme, the sync state, connected apps, the release
 * -- so the rail can give its height to lists.
 *
 * One continuous page of sections, not cards or tabs: there are five things
 * here and a structure for fifty would be the loudest thing on it. Deleting
 * the account is last, inside a Danger zone that is closed on every visit.
 */
export function settingsScreen(shell: Shell, account: AccountFacet): Screen {
  // ---- Account ------------------------------------------------------------

  // The name people see beside what this account adds to a shared list
  // (g73). It can be changed and never emptied; the first share link asks
  // for one when there is none.
  const nameText = el("bdi");
  const name = el("p", { class: "setting-value settings-name", children: [nameText] });
  const changeName = el("button", {
    class: "secondary change-name-open",
    attrs: { type: "button" },
    on: {
      click: () => {
        if (account.formOpen()) {
          shell.announce("Finish the open form before changing your name.");
          return;
        }
        openAccountName(
          { openPopup: shell.openPopup, announce: shell.announce, updateUser: account.updateUser },
          shell.user,
          changeName,
        );
      },
    },
  });
  const paintName = (value: string | null | undefined) => {
    const named = typeof value === "string" && value.length > 0;
    nameText.textContent = named ? value : ACCOUNT_NAME.none;
    changeName.textContent = named ? ACCOUNT_NAME.changeTitle : ACCOUNT_NAME.addTitle;
  };
  paintName(shell.user.name);

  const email = el("p", { class: "setting-value settings-email", text: shell.user.email });
  const changeEmail = el("button", {
    class: "secondary change-email-open",
    text: "Change email",
    attrs: { type: "button" },
    on: {
      click: () => {
        if (account.formOpen()) {
          shell.announce("Finish the open form before changing your email address.");
          return;
        }
        openEmailChange(
          { openPopup: shell.openPopup, announce: shell.announce, updateUser: account.updateUser },
          shell.user,
          changeEmail,
        );
      },
    },
  });
  const stopUser = account.onUser((user) => {
    email.textContent = user.email;
    paintName(user.name);
  });

  // Sends what this device has not sent before it signs out, and asks before
  // losing whatever it could not send (g60).
  const signOut = signOutControl(shell);

  // ---- Appearance ---------------------------------------------------------

  // The switch names the theme it goes to (g21), so the page says which one is
  // on beside it; one without the other reads as the current state. The value
  // says where it applies (g56): the choice is kept on this device only.
  const themeNow = el("p", { class: "setting-value theme-now" });
  const paintTheme = (theme: ReturnType<typeof currentTheme>) => {
    themeNow.textContent = theme === "dark" ? "Dark on this device" : "Light on this device";
  };
  paintTheme(currentTheme());
  const stopTheme = onTheme(paintTheme);
  const toggle = themeToggle();

  // ---- Connection ---------------------------------------------------------

  const sync = syncLine(shell.store, "page");
  const connections = connectionsSection({ confirm: shell.confirm, announce: shell.announce });

  // ---- Danger zone --------------------------------------------------------

  // A native disclosure: closed, its contents are not rendered, so they are out
  // of the tab order and the accessibility tree without anything to keep in
  // step. Built closed on every mount and never persisted open. Opening it only
  // opens it; nothing listens for the toggle.
  const summary = el("summary", {
    class: "danger-summary",
    children: [el("span", { text: "Danger zone" }), icon("chevron")],
  });
  const deleteAccount = el("button", {
    class: "danger delete-account-open",
    text: "Delete account",
    attrs: { type: "button" },
    on: {
      click: () => {
        // A disconnect in flight holds the page too: asked now, rather than
        // refused after the email has been typed.
        if (account.draftOpen() || connections.busy()) {
          shell.announce("Finish the open form before deleting your account.");
          return;
        }
        // The popup focuses the typed-email field, never its red button, and
        // Cancel comes back here while this is still on the page.
        openDeleteAccount(
          { openPopup: shell.openPopup, announce: shell.announce, remove: account.deleteAccount },
          shell.user,
          () => (deleteAccount.isConnected && danger.open ? deleteAccount : summary),
        );
      },
    },
  });
  const danger = el("details", {
    class: "danger-zone",
    children: [
      summary,
      el("div", {
        class: "danger-body",
        children: [
          el("p", {
            class: "popup-note",
            text: "Deleting your account removes it with all its lists and items, and their history. You will be asked to type your email address before anything is deleted.",
          }),
          deleteAccount,
        ],
      }),
    ],
  });

  const section = (title: string, ...children: HTMLElement[]) =>
    el("section", { class: "settings-section", children: [el("h2", { text: title }), ...children] });

  const row = (label: string, value: HTMLElement | undefined, control?: HTMLElement) =>
    el("div", {
      class: "setting-row",
      children: [
        el("div", {
          class: "setting-text",
          children: [el("p", { class: "setting-label", text: label }), ...(value ? [value] : [])],
        }),
        ...(control ? [control] : []),
      ],
    });

  // Who changed what, and when (g110): the account's history, which is also
  // the way to a deleted list's. Shown only while the server lets it be read.
  const activityRow = row(
    "Activity",
    el("p", { class: "setting-value", text: "Who changed your lists, and when" }),
    el("button", {
      class: "secondary activity-open",
      attrs: { type: "button" },
      children: [icon("history"), el("span", { text: "View activity" })],
      on: { click: () => openActivity() },
    }),
  );
  activityRow.hidden = !shell.store.activityAvailable();
  const stopActivity = shell.store.onActivityAvailable((available) => { activityRow.hidden = !available; });

  const node = el("section", {
    class: "screen settings",
    children: [
      el("header", { class: "settings-head", children: [el("h1", { text: "Settings" })] }),
      section("Account", row("Name", name, changeName), row("Email", email, changeEmail), row("Session", el("p", { class: "setting-value", text: "Signed in on this device" }), signOut.node), activityRow),
      section("Appearance", row("Theme", themeNow, toggle.node)),
      section("Connection", row("Sync", sync.node), connections.node),
      // The number vite.config.ts injected from package.json at build time:
      // nothing is fetched, so a cached older app says its own version until
      // the new bundle activates. Plain text, so it can be copied into a bug
      // report.
      section(
        "About",
        el("div", {
          class: "setting-row",
          children: [el("p", { class: "setting-value release", text: `Version ${__APP_VERSION__}` })],
        }),
        el("div", { class: "setting-row", children: [policyLinks()] }),
      ),
      danger,
    ],
  });

  return {
    node,
    // Nothing on this page is selectable, but a popup closing steps history
    // back onto this same route, and without `update` the frame would rebuild
    // the page under it: the Danger zone would close and the focus would go
    // back to a button that is no longer there.
    update() {},
    busy: () => connections.busy(),
    dispose() {
      stopActivity();
      stopUser();
      stopTheme();
      toggle.dispose();
      sync.dispose();
      connections.dispose();
      signOut.dispose();
    },
  };
}
